import React, { useCallback, useEffect, useState } from "react";
import { api, formatDistance } from "@/lib/api";
import { FileImage } from "@/components/FileImage";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { MapPin, Camera, Clock, PlusCircle } from "lucide-react";

function fmt(ts) {
  if (!ts) return "—";
  try {
    return new Date(ts).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
  } catch { return ts; }
}

// An attempt is eligible for supplemental evidence ONLY when it has an evidence
// exception: a missing service-area photo, or a GPS/location exception.
export function attemptNeedsSupplemental(a) {
  const g = a.gps || {};
  const missingPhoto = !((a.photos || []).length);
  const gpsMissing = g.lat == null || g.lng == null;
  const gpsFlagged = g?.validation?.status === "flagged";
  return missingPhoto || gpsMissing || gpsFlagged;
}

function SupplementalCard({ ev }) {
  const g = ev.gps || {};
  return (
    <div data-testid={`supplemental-card-${ev.id}`} className="rounded-lg border border-amber-200 bg-amber-50/50 p-3">
      <div className="flex items-center gap-1.5">
        <span className="inline-flex items-center gap-1 rounded-full border border-amber-300 bg-amber-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-amber-800">
          <PlusCircle className="h-3 w-3" /> Supplemental location evidence
        </span>
      </div>
      <p className="mt-1.5 flex items-center gap-1.5 font-mono-arps text-[11px] text-slate-600">
        <Clock className="h-3.5 w-3.5 text-amber-600" />
        <span className="font-semibold text-slate-500">Captured (server time):</span> {fmt(ev.captured_at)}
      </p>
      <p className="mt-0.5 text-[11px] text-slate-500">by {ev.server_name || "Process server"}</p>
      <div className="mt-2 flex flex-wrap gap-2">
        {g.lat != null && g.lng != null ? (
          <span className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-2 py-1 font-mono-arps text-[11px] text-slate-600">
            <MapPin className="h-3.5 w-3.5 text-emerald-600" />
            {Number(g.lat).toFixed(4)}, {Number(g.lng).toFixed(4)} {g.accuracy != null ? `±${Math.round(g.accuracy)}m` : ""}
          </span>
        ) : <span className="rounded-md border border-slate-200 bg-white px-2 py-1 text-[11px] text-slate-400">No GPS</span>}
        {g.validation?.status && (
          <span className={`inline-flex items-center gap-1 rounded-md border px-2 py-1 text-[11px] font-semibold ${
            g.validation.status === "pass" ? "border-emerald-200 bg-emerald-50 text-emerald-700" :
            g.validation.status === "flagged" ? "border-red-200 bg-red-50 text-red-700" : "border-slate-200 bg-white text-slate-500"}`}>
            {g.validation.status === "pass" ? "Location verified" : g.validation.status === "flagged"
              ? `Outside service location${g.validation.distance_m != null ? ` · ${formatDistance(g.validation.distance_m)}` : ""}` : "Proximity not validated"}
          </span>
        )}
      </div>
      {ev.photo_id && (
        <div className="mt-2">
          <Dialog>
            <DialogTrigger asChild>
              <button data-testid={`supplemental-photo-${ev.id}`} className="overflow-hidden rounded-md border border-slate-200 transition hover:ring-2 hover:ring-amber-400">
                <FileImage fileId={ev.photo_id} className="h-16 w-16 object-cover" alt="Supplemental evidence" />
              </button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader><DialogTitle>Supplemental location photo</DialogTitle></DialogHeader>
              <FileImage fileId={ev.photo_id} className="w-full rounded-lg" alt="Supplemental evidence full" />
            </DialogContent>
          </Dialog>
        </div>
      )}
      {ev.note && <p className="mt-2 rounded-md bg-white/70 px-2.5 py-1.5 text-xs text-slate-600">{ev.note}</p>}
    </div>
  );
}

// Read-only list of supplemental evidence for one attempt (identified by attempt_number).
export function SupplementalEvidenceList({ orderId, attemptNumber, refreshKey = 0 }) {
  const [items, setItems] = useState([]);
  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/orders/${orderId}/supplemental-evidence`);
      setItems((data || []).filter((e) => e.attempt_number === attemptNumber));
    } catch { setItems([]); }
  }, [orderId, attemptNumber]);
  useEffect(() => { load(); }, [load, refreshKey]);

  if (!items.length) return null;
  return (
    <div className="mt-3 space-y-2 border-t border-dashed border-amber-200 pt-3" data-testid={`supplemental-list-${attemptNumber}`}>
      <p className="text-[11px] font-semibold uppercase tracking-wide text-amber-700">Supplemental location evidence (captured after this attempt)</p>
      {items.map((ev) => <SupplementalCard key={ev.id} ev={ev} />)}
    </div>
  );
}
